import { DatePipe } from '@angular/common';
import { CostBasisMethods } from '@interfaces/models/cost-basis-methods';
import {
  AccountTypeNames,
  AccountTypes,
  IReceipt,
  ISelectedAccount,
  TooltipReceipt,
} from '@shared/models';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import {
  getIsBrokerageFirmRecipient,
  getIsCharityRecipient,
  getIsTransferAgentRecipient,
  getRecipientAccountNumber,
  getRecipientAnonymousGift,
  getRecipientCity,
  getRecipientDTCNumber,
  getRecipientFirm,
  getRecipientName,
  getRecipientSSN,
  getRecipientState,
  getRecipientStreetAddress,
  getRecipientTransferAgentName,
  getRecipientZipcode,
  GiftingRecipient,
  GiftingState,
} from '@store/reducers/gifting/gifting.reducer';
import {
  getAccountTypeSelection,
  getIsGiftingFlow,
  getIsVanguardGiftingFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { GiftingMethodEnum } from 'src/app/secure/gifting-method/gifting-position.constants';

import { ReviewContent } from '../../../../../shared/services/content/content-jsons/review-content';
import { toSentenceLowerCase } from '../cashier-nonretirement';
import { formatProductTypeName } from '../internal-transfer/cashier-internal-transfer';

const datePipe = new DatePipe('en-US');

const getAccountTypeName = (accountType: AccountTypes): AccountTypeNames =>
  AccountTypeNames[accountType];

export function buildAccountFromGifting(
  triageState: TriageState,
  accountFrom: ISelectedAccount,
  editable: boolean,
): IReceipt {
  const accountTypeName = getAccountTypeName(getAccountTypeSelection(triageState));
  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: editable,
    editRoute: 'secure/gifting/assets',
    sectionEvidenceID: 'accountFrom',
    rows: [
      {
        label: 'Account type',
        values: [
          `${accountTypeName} ${formatProductTypeName(accountFrom?.accountData?.productType)} account`,
        ],
        evidenceID: 'fromAccountType',
      },
      {
        label: 'Account number',
        values: [`${accountFrom?.accountNumber}`],
        evidenceID: 'fromAccountNumber',
      },
    ],
  };

  if (accountFrom?.accountData?.nickname) {
    receipt.rows.push({
      label: 'Account nickname',
      values: [accountFrom.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  return receipt;
}

const costBasisDictionary = {
  SpecID: 'Specific identification (SpecID)',
  FIFO: 'First in, first out (FIFO)',
  HIFO: 'Highest in, first out (HIFO)',
  AvgCost: 'Average cost (AvgCost)',
  MinTax: 'Minimum Tax (MinTax)',
};

const getMailingAddress = (recipient: GiftingRecipient) =>
  getRecipientStreetAddress(recipient) +
  '<br>' +
  getRecipientCity(recipient) +
  ', ' +
  getRecipientState(recipient) +
  ' ' +
  getRecipientZipcode(recipient);

const getFormattedRecipientDateOfBirth = (recipient: GiftingRecipient) => {
  return datePipe.transform(recipient.dateOfBirth, 'MMMM d, y');
};

function getShareDollarAmountForPosition(
  position: CostBasisPosition,
  isVanguardGifting: boolean,
): string {
  if (position.selectedCostBasisMethod === CostBasisMethods.SPECID)
    return `${position.taxLotsTotalShares} share${+position.taxLotsTotalShares === 1 ? '' : 's'} | ${
      position.taxLotsTotalMarketValue
    }${isVanguardGifting ? '*' : ''}`;
  if (position.giftingMethod === 'DOLLAR_AMOUNT') return String(position.selectedAmount);
  return `${position.selectedAmount} share${+position.selectedAmount === 1 ? '' : 's'} | ${
    position.sharesTotalMarketValue
  }${isVanguardGifting ? '*' : ''}`;
}

export function buildGiftReceipt(
  selectedPositions: CostBasisPosition[],
  triageState: TriageState,
  giftingState: GiftingState,
  editable: boolean,
): IReceipt {
  const receipt: IReceipt = {
    header: 'Gift',
    editable: editable,
    editRoute: 'secure/gifting/assets',
    sectionEvidenceID: 'gift',
    rows: [],
  };
  const isVanguardGifting = getIsVanguardGiftingFlow(triageState);

  selectedPositions.forEach((position) => {
    const positionHeader = getPositionHeader(position);

    receipt.rows.push(
      {
        label: '',
        values: [positionHeader],
        evidenceID: 'positionName',
        hideBorder: true,
        customLineHeight: true,
      },
      {
        label: 'Gift method',
        values: [toSentenceLowerCase(position.giftingMethod).replace('_', ' ')],
        evidenceID: 'giftMethod',
        hideBorder: true,
        customLineHeight: true,
      },
    );

    if (!(position.settlementFund || !position.selectedCostBasisMethod)) {
      receipt.rows.push({
        label: 'Cost basis method',
        values: [costBasisDictionary[position.selectedCostBasisMethod]],
        evidenceID: 'costBasisMethod',
        hideBorder: true,
        customLineHeight: true,
      });
    }

    receipt.rows.push({
      label: ReviewContent.sharesDollarAmountLabel,
      values: [getShareDollarAmountForPosition(position, isVanguardGifting)],
      evidenceID: 'shareDollarAmount',
      hideBorder: true,
      customLineHeight: true,
    });

    position.taxLots?.forEach((lot) => {
      if (lot.quantity) {
        receipt.rows.push(
          {
            label: 'Date acquired',
            values: [lot.aquiredDate],
            evidenceID: 'aquiredDate',
            hideBorder: true,
            customLineHeight: true,
          },
          {
            label: 'Shares',
            values: [lot.quantity.toFixed(3)],
            evidenceID: 'lotShares',
            hideBorder: true,
            customLineHeight: true,
          },
          {
            label: 'Lot Id',
            values: [lot.lotID],
            evidenceID: 'lotId',
            hideRow: true,
          },
        );
      }
    });

    receipt.rows.push({
      label: '',
      values: [''],
      evidenceID: '',
      customLineHeight: true,
    });
  });

  receipt.rows.push({
    label: 'Est. total gift value',
    values: [giftingState.totalGiftAmount + (isVanguardGifting ? '*' : '')],
    evidenceID: 'totalGift',
    isLabelBold: true,
  });
  return receipt;
}

export function buildAccountToGifting(
  recipient: GiftingRecipient,
  triageState: TriageState,
  editable: boolean,
): IReceipt {
  const accountToHeader = {
    header: 'Transferring to',
    editable: editable,
    editRoute: 'secure/gifting/recipient-triage',
    sectionEvidenceID: 'accountTo',
  };
  switch (true) {
    case getIsVanguardGiftingFlow(triageState):
      return {
        ...accountToHeader,
        rows: [
          {
            label: 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsCharityRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: 'Name of receiving firm',
            values: [getRecipientFirm(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: 'Anonymously gifting',
            values: [getRecipientAnonymousGift(recipient)],
            evidenceID: 'toAccountAnonymousGift',
          },
          {
            label: 'DTC number',
            values: [getRecipientDTCNumber(recipient)],
            evidenceID: 'toAccountDTCNumber',
          },
          {
            label: 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsBrokerageFirmRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: 'Name of receiving firm',
            values: [getRecipientFirm(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: 'DTC number',
            values: [getRecipientDTCNumber(recipient)],
            evidenceID: 'toAccountDTCNumber',
          },
          {
            label: 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsTransferAgentRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: 'Name of transfer agent',
            values: [getRecipientTransferAgentName(recipient)],
            evidenceID: 'toAccountTransferAgentName',
          },
          {
            label: 'Name of individual trust or organization',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: 'Receiving account number',
            values: [getRecipientAccountNumber(recipient) || 'N/A'],
            evidenceID: 'toAccountNumber',
          },
          {
            label: 'Mailing address',
            values: [getMailingAddress(recipient)],
            evidenceID: 'toAccountMailingAddress',
          },
          {
            label: 'Social Security Number',
            values: [getRecipientSSN(recipient)],
            evidenceID: 'toAccountSNN',
          },
          {
            label: 'Date of birth',
            values: [getFormattedRecipientDateOfBirth(recipient) || 'N/A'],
            evidenceID: 'toAccountDateOfBirth',
          },
        ],
      };
    default:
      return {
        ...accountToHeader,
        rows: [
          {
            label: 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
  }
}

function getPositionHeader(position: CostBasisPosition) {
  return position.ticker && position.securityName
    ? `${position.ticker} &mdash; ${position.securityName}`
    : position.ticker || position.securityName;
}

export function buildAdditionalTaxLot(
  selectedPositions: CostBasisPosition[],
  triageState: TriageState,
  editable: boolean,
): TooltipReceipt {
  const isGifting = getIsGiftingFlow(triageState);

  if (!isGifting || selectedPositions.length === 0) return null;

  const receipt: TooltipReceipt = {
    header: 'Backup tax lot for fractional shares',
    editable: editable,
    editRoute: 'secure/gifting/method/1',
    sectionEvidenceID: 'additionalTaxLot',
    toolTip:
      'We will use the chosen lot for any additional fractional shares needed for your gift.',
    rows: [],
  };
  const isVanguardGifting = getIsVanguardGiftingFlow(triageState);

  selectedPositions.forEach((position) => {
    const positionHeader = getPositionHeader(position);
    const lot = position.additionalTaxLot;
    const isEligible = isDollarAmountSpecIdFlow(position);
    if (isEligible && lot) {
      const modifiedPosition: CostBasisPosition = {
        ...position,
        taxLotsTotalShares: '' + lot.quantity,
        taxLotsTotalMarketValue: lot.formattedMarketValue,
      };
      receipt.rows.push(
        {
          label: '',
          values: [positionHeader],
          evidenceID: 'positionName',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: ReviewContent.sharesDollarAmountLabel,
          values: [getShareDollarAmountForPosition(modifiedPosition, isVanguardGifting)],
          evidenceID: 'shareDollarAmount',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: 'Date acquired',
          values: [lot.aquiredDate],
          evidenceID: 'aquiredDate',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: 'Lot Id',
          values: [lot.lotID],
          evidenceID: 'lotId',
          hideRow: true,
        },
        {
          label: '',
          values: [''],
          evidenceID: '',
          customLineHeight: true,
        },
      );
    }
  });
  if (receipt.rows.length === 0) return null;

  return receipt;
}

export const isDollarAmountSpecIdFlow = (position: CostBasisPosition) => {
  return (
    position.giftingMethod === GiftingMethodEnum.DOLLAR_AMOUNT &&
    position.selectedCostBasisMethod === CostBasisMethods.SPECID
  );
};
